"use client";

import React from "react";
import { motion } from "framer-motion";
import { ShieldCheck, House as Home, Sparkles, Clock, ArrowRight, Phone, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const features = [{
  id: 1,
  title: "Property & garden protection",
  description: "Sole pads and protective boards under every standard to safeguard your driveway, patio, and lawns.",
  icon: ShieldCheck
}, {
  id: 2,
  title: "Polite, punctual crews",
  description: "Considerate, accredited scaffolders who respect your family routine and your neighbours.",
  icon: Sparkles
}, {
  id: 3,
  title: "Prompt strike dates",
  description: "Once your builder or roofer signs off the job, we schedule swift dismantle so your home is clear.",
  icon: Clock
}, {
  id: 4,
  title: "Full project versatility",
  description: "Tailored structures for two-storey extensions, chimney stacks, dormer loft conversions, and re-roofs.",
  icon: Home
}];
const checklistItems = ["Insured, accredited working standards and full safety documentation", "NASC and CHAS accredited working standards", "On-site measurements and straightforward, transparent quotes.", "Careful layout planning around conservatories, driveways, and boundaries"];
function ResidentialContent() {
  const {
    posts,
    loading
  } = usePosts();
  const targetPhoto = posts?.find((p: any) => p?.customFields?.photo_name === "Residential Home Image" || p?.acf?.photo_name === "Residential Home Image");
  const dynamicImage = targetPhoto?.customFields?.image || targetPhoto?.acf?.image;
  const dynamicAlt = targetPhoto?.customFields?.alt_text || targetPhoto?.acf?.alt_text;
  const fallbackSrc = "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/domestic_scaffold_residential_house.png";
  const fallbackAlt = "Domestic scaffolding structure erected on a residential home for roof and extension work";
  const imageSrc = dynamicImage || fallbackSrc;
  const imageAlt = dynamicAlt || fallbackAlt;
  const isDynamic = Boolean(dynamicImage);
  return <section id="residential-overview" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border transition-colors">
      <div className="container mx-auto px-5 sm:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 mb-3">
            <span className="h-0.5 w-6 bg-primary rounded-full" />
            <span className="text-xs md:text-sm font-bold uppercase tracking-wider text-muted-foreground font-default">
              Residential Scaffolding
            </span>
          </div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-foreground font-default mb-5 leading-tight">
            Scaffolding built with care for your home.
          </h1>
          <p className="text-base md:text-lg text-muted-foreground font-default leading-relaxed">
            From extensions and re-pointing to chimney stacks and solar panel fittings, we install safe, tidy access frameworks across Berkshire, Surrey, Hampshire, and London that protect your grounds and come down promptly when work is done.
          </p>
        </div>

        {/* Main Content Grid: Text + Image Split */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center mb-16">
          {/* Left Column: Key Features Matrix */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.45,
          ease: "easeOut"
        }} className="lg:col-span-6 space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {features.map((feature, i) => {
              const IconComponent = feature.icon;
              return <Card key={feature.id} data-index={i} className="border border-border bg-card rounded-lg shadow-xs hover:border-primary/40 transition-[border-color,box-shadow] duration-200">
                    <CardContent className="p-5 flex flex-col justify-start">
                      <div className="size-10 rounded-md bg-accent text-primary flex items-center justify-center mb-4">
                        <IconComponent className="size-5" />
                      </div>
                      <h3 className="text-base font-bold text-foreground font-default mb-1.5">
                        {feature.title}
                      </h3>
                      <p className="text-sm text-muted-foreground font-default leading-snug">
                        {feature.description}
                      </p>
                    </CardContent>
                  </Card>;
            })}
            </div>

            {/* Checklist Strip */}
            <div className="bg-card border border-border rounded-lg p-6 space-y-3 shadow-xs">
              <h4 className="text-sm font-bold uppercase tracking-wider text-foreground font-default">
                Homeowner assurances
              </h4>
              <ul className="space-y-2.5">
                {checklistItems.map((item, i) => <li key={i} data-index={i} className="flex items-start gap-2.5 text-sm text-muted-foreground font-default">
                    <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>)}
              </ul>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap items-center gap-4 pt-2">
              <Button asChild size="lg" className="bg-primary text-primary-foreground font-semibold hover:bg-primary/90 h-12 px-6 rounded-md shadow-sm">
                <Link to="/ContactRequestQuotePage" className="inline-flex items-center gap-2">
                  <span>Request a free quote</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="border-border text-foreground font-semibold hover:bg-accent hover:text-foreground h-12 px-6 rounded-md">
                <a href="tel:01344929900" className="inline-flex items-center gap-2 text-foreground">
                  <Phone className="size-4 text-primary" />
                  <span>Call our team</span>
                </a>
              </Button>
            </div>
          </motion.div>

          {/* Right Column: Domestic Scaffolding Imagery & Callout */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.45,
          ease: "easeOut",
          delay: 0.1
        }} className="lg:col-span-6 relative">
            <div className="relative rounded-lg overflow-hidden border border-border shadow-md bg-muted">
              {loading ? <div className="w-full h-[380px] sm:h-[460px] lg:h-[500px] bg-muted animate-pulse" /> : <img src={imageSrc} data-wvc-srcset={isDynamic ? "" : "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/domestic_scaffold_residential_house.png-wvc-srcset"} data-wvc-sizes={isDynamic ? "" : "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/domestic_scaffold_residential_house.png-wvc-sizes"} alt={imageAlt} loading="lazy" decoding="async" className="w-full h-[380px] sm:h-[460px] lg:h-[500px] object-cover" />}
              {/* Overlay Accent Card */}
              <div className="absolute bottom-4 left-4 right-4 sm:bottom-6 sm:left-6 sm:right-6 bg-secondary/95 backdrop-blur-xs text-secondary-foreground p-4 sm:p-5 rounded-md border border-secondary/30 shadow-lg">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="text-xs font-bold uppercase tracking-wider text-primary mb-1">
                      Family-run reliability
                    </div>
                    <p className="text-sm font-medium leading-snug">
                      Over 20 years of hands-on leadership under Pete Wood, treating private residences with rigorous safety and care.
                    </p>
                  </div>
                  <div className="hidden sm:flex flex-col items-end shrink-0 text-right">
                    <span className="text-xs text-secondary-foreground/70">Coverage</span>
                    <span className="text-xs font-bold text-secondary-foreground">South East England</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}
export default function Residential() {
  const wp_query_params = {
    post_type: "site_photo",
    per_page: 100,
    status: ["publish"]
  };
  const wp_query = new WP_Query(wp_query_params);
  return <PostsProvider wp_query={{
    post_type: "site_photo",
    per_page: 100,
    status: ["publish"]
  }}>
      <ResidentialContent />
    </PostsProvider>;
}